import { useEffect, useId, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";

import { isProOrUltraPlan } from "@/components/shared/PaidPlanRoute";
import { InvoiceEmailAddressListInput } from "@/components/shared/InvoiceEmailAddressListInput";
import { InvoiceEmailCustomBodyEditor } from "@/components/shared/InvoiceEmailCustomBodyEditor";
import { InvoiceEmailPreviewButton } from "@/components/shared/InvoiceEmailPreviewButton";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  PDF_FILENAME_TOKENS,
  previewPdfFilename,
} from "@/lib/pdf-filename-preview";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  clientFormSchema,
  ClientFormValues,
  CLIENT_COUNTRY_OPTIONS,
  CLIENT_CURRENCY_OPTIONS,
  CLIENT_DOCUMENT_TYPES,
  CLIENT_PAYMENT_TERM_OPTIONS,
  defaultClientFormValues,
  normalizeClientCi,
  normalizeClientRut,
} from "@/lib/client-create-form";
import { clientKeys, tenantKeys } from "@/lib/queryKeys";
import { getDefaultInvoiceEmailCustomHtml } from "@/utils/invoice-email-custom-default-html";
import { htmlVisibleLength } from "@/lib/html-text";
import {
  getDefaultInvoiceEmailSubjectTemplate,
  previewInvoiceEmailSubject,
  resolveTenantLangForInvoiceEmail,
} from "@/utils/invoice-email-subject-default";
import {
  parseInvoiceEmailsFromApi,
  serializeInvoiceEmailsForApi,
} from "@/utils/invoice-email-addresses";
import { normalizeInvoiceEmailTemplateForForm } from "@/utils/invoice-email-template-form";
import { useAuth } from "@/contexts/AuthContext";
import { BrandSelectField } from "@/components/features/brands/BrandSelectField";
import { getTenantCurrent } from "@/services/authService";
import {
  getClient,
  updateClient,
  type CustomerDocumentType,
} from "@/services/clientService";

export function EditClientPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: client, isLoading: loading } = useQuery({
    queryKey: clientKeys.detail(id ?? ""),
    queryFn: () => getClient(id!),
    enabled: Boolean(id),
  });

  const { user } = useAuth();
  const { data: tenant, isFetched: tenantPlanFetched } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const tenantLang = resolveTenantLangForInvoiceEmail(tenant?.language);
  const canUseInvoiceEmailTemplates =
    tenantPlanFetched && isProOrUltraPlan(tenant?.planId);

  const [invoiceEmailBodyEditorKey, setInvoiceEmailBodyEditorKey] = useState(0);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: defaultClientFormValues,
  });

  const invoiceEmailRadioId = useId();

  const documentType = watch("documentType");
  const countryCode = watch("countryCode");

  useEffect(() => {
    const code = countryCode?.trim().toUpperCase();
    if (code && code !== "UY") {
      setValue("documentType", "EXT");
    }
  }, [countryCode, setValue]);

  useEffect(() => {
    if (client) {
      const docType = (client.documentType ?? "RUT") as CustomerDocumentType;
      const template = normalizeInvoiceEmailTemplateForForm(
        client.invoiceEmailTemplate,
        canUseInvoiceEmailTemplates
      );
      const rawBody = client.invoiceEmailCustomBody ?? "";
      const invoiceEmailCustomBody =
        template === "custom" && !rawBody.trim()
          ? getDefaultInvoiceEmailCustomHtml(t)
          : rawBody;
      const rawSubject = client.invoiceEmailCustomSubject ?? "";
      const invoiceEmailCustomSubject =
        template === "custom" && !rawSubject.trim()
          ? getDefaultInvoiceEmailSubjectTemplate(tenantLang)
          : rawSubject;
      reset({
        legalName: client.legalName ?? "",
        displayName: client.displayName ?? "",
        documentType: docType,
        rut: client.rut ?? "",
        countryCode: client.countryCode ?? "",
        docRecepExt: client.docRecepExt ?? "",
        address: client.address ?? "",
        ciudad: client.ciudad ?? "",
        departamento: client.departamento ?? "",
        email: client.email ?? "",
        phone: client.phone ?? "",
        defaultCurrency: client.defaultCurrency ?? "UYU",
        paymentTermDays: client.paymentTermDays ?? 0,
        discountPercent:
          client.discountPercent != null ? String(client.discountPercent) : "",
        sendInvoiceByEmail: client.sendInvoiceByEmail ?? false,
        autoSendInvoiceEmail: client.autoSendInvoiceEmail ?? true,
        invoiceEmailChoice:
          client.invoiceEmail?.trim() ? "different" : "contact",
        invoiceEmail: parseInvoiceEmailsFromApi(client.invoiceEmail),
        invoiceEmailCc: parseInvoiceEmailsFromApi(client.invoiceEmailCc),
        invoiceEmailTemplate: template,
        invoiceEmailCustomBody,
        invoiceEmailCustomSubject,
        invoiceEmailIncludeCompanyLogo: client.invoiceEmailIncludeCompanyLogo ?? true,
        invoiceEmailIncludeInvoiceDetails:
          client.invoiceEmailIncludeInvoiceDetails ?? true,
        pdfFilenameTemplate: client.pdfFilenameTemplate ?? "",
        language: client.language ?? "es",
        defaultBrandId: client.defaultBrandId ?? "",
      });
      setInvoiceEmailBodyEditorKey((k) => k + 1);
    }
  }, [client, reset, canUseInvoiceEmailTemplates, t, tenantLang]);

  const updateMutation = useMutation({
    mutationFn: (values: ClientFormValues) => {
      const payload: Parameters<typeof updateClient>[1] = {
        legalName: values.legalName.trim().toUpperCase(),
        displayName: values.displayName?.trim() || undefined,
        documentType: values.documentType,
        address: values.address?.trim() || undefined,
        ciudad: values.ciudad?.trim() || undefined,
        departamento: values.departamento?.trim() || undefined,
        // null, not undefined: clearing the field has to actually clear the
        // stored email, and undefined would leave the old one in place.
        email: values.email?.trim() || null,
        phone: values.phone?.trim() || undefined,
      };
      if (values.documentType === "EXT") {
        payload.countryCode = values.countryCode?.trim() || undefined;
        payload.docRecepExt =
          values.docRecepExt?.trim()?.slice(0, 20) || undefined;
      } else {
        payload.rut =
          values.documentType === "RUT"
            ? normalizeClientRut(values.rut)
            : normalizeClientCi(values.rut);
      }
      if (values.defaultCurrency !== undefined)
        payload.defaultCurrency = values.defaultCurrency;
      if (values.paymentTermDays !== undefined)
        payload.paymentTermDays = values.paymentTermDays;
      if (values.discountPercent !== undefined && values.discountPercent !== "") {
        const n = Number(values.discountPercent);
        payload.discountPercent = Number.isNaN(n) ? undefined : n;
      }
      if (values.sendInvoiceByEmail !== undefined)
        payload.sendInvoiceByEmail = values.sendInvoiceByEmail;
      if (values.autoSendInvoiceEmail !== undefined)
        payload.autoSendInvoiceEmail = values.autoSendInvoiceEmail;
      if (
        values.sendInvoiceByEmail &&
        values.invoiceEmailChoice === "different" &&
        values.invoiceEmail &&
        values.invoiceEmail.length > 0
      ) {
        payload.invoiceEmail = serializeInvoiceEmailsForApi(values.invoiceEmail);
      } else {
        payload.invoiceEmail = null;
      }
      if (
        values.sendInvoiceByEmail &&
        values.invoiceEmailChoice === "different"
      ) {
        const ccList = values.invoiceEmailCc?.filter((e) => e.trim()) ?? [];
        payload.invoiceEmailCc =
          ccList.length > 0 ? serializeInvoiceEmailsForApi(ccList) : null;
      } else {
        payload.invoiceEmailCc = null;
      }
      if (values.sendInvoiceByEmail === true && canUseInvoiceEmailTemplates) {
        payload.invoiceEmailTemplate = values.invoiceEmailTemplate ?? "default";
        if (values.invoiceEmailTemplate === "custom") {
          payload.invoiceEmailCustomBody = values.invoiceEmailCustomBody?.trim() ?? "";
          payload.invoiceEmailCustomSubject =
            values.invoiceEmailCustomSubject?.trim() || null;
          payload.invoiceEmailIncludeCompanyLogo =
            values.invoiceEmailIncludeCompanyLogo ?? true;
          payload.invoiceEmailIncludeInvoiceDetails =
            values.invoiceEmailIncludeInvoiceDetails ?? true;
        } else {
          payload.invoiceEmailCustomBody = null;
          payload.invoiceEmailCustomSubject = null;
        }
      }
      payload.pdfFilenameTemplate = values.pdfFilenameTemplate?.trim() || null;
      payload.language = values.language ?? "es";
      // null clears it; the API reads undefined as "leave alone".
      payload.defaultBrandId = values.defaultBrandId?.trim() || null;
      return updateClient(id!, payload);
    },
    onSuccess: () => {
      toast.success(t("clients.edit.updated", "Client updated."));
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(id!) });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
    onError: () => {
      toast.error(
        t("clients.edit.error", "Could not update client. Please try again.")
      );
    },
  });

  const onSubmit = (values: ClientFormValues): void => {
    if (id) updateMutation.mutate(values);
  };

  const showRutOrCi = documentType === "RUT" || documentType === "CI";
  const showExt = documentType === "EXT";

  if (!id) {
    return (
      <div className="fx-content">
        <PageHeader
          title={t("clients.edit.invalidId", "Invalid client")}
          onBack={() => navigate(-1)}
        />
      </div>
    );
  }

  if (loading) {
    return <LoadingPage />;
  }

  if (!loading && !client) {
    return (
      <div className="fx-content">
        <PageHeader
          title={t("clients.edit.title", "Edit client")}
          onBack={() => navigate(-1)}
        />
        <p className="mt-4 text-sm text-muted-foreground">
          {t("clients.edit.notFound", "Client not found.")}
        </p>
      </div>
    );
  }

  if (!client) {
    return <LoadingPage />;
  }

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("clients.edit.title", "Edit client")}
        description={client.displayName ?? client.legalName}
        onBack={() => navigate(-1)}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>
              {t("clients.form.legalInfo", "Legal information")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t("clients.form.documentType", "Document type")} *</Label>
              <Controller
                name="documentType"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_DOCUMENT_TYPES.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {showRutOrCi && (
              <div className="space-y-2">
                <Label htmlFor="rut">
                  {documentType === "RUT"
                    ? t("clients.form.taxId", "Tax ID (RUT)") + " *"
                    : t("clients.form.ci", "Cédula (CI)") + " *"}
                </Label>
                <Input
                  id="rut"
                  {...register("rut")}
                  placeholder={
                    documentType === "RUT"
                      ? t("clients.form.taxIdPlaceholder", "12 digits")
                      : t("clients.form.ciPlaceholder", "7 or 8 digits")
                  }
                  maxLength={documentType === "RUT" ? 12 : 8}
                  className={errors.rut ? "border-destructive" : ""}
                />
                {errors.rut && (
                  <p className="text-sm text-destructive">
                    {errors.rut.message}
                  </p>
                )}
              </div>
            )}

            {showExt && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="countryCode">
                    {t("clients.form.country", "Country")}
                  </Label>
                  <Controller
                    name="countryCode"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value ?? ""}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger id="countryCode">
                          <SelectValue
                            placeholder={t("clients.form.countryPlaceholder")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {CLIENT_COUNTRY_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="docRecepExt">
                    {t("clients.form.docRecepExt", "Document (passport, etc.)")} *
                  </Label>
                  <Input
                    id="docRecepExt"
                    {...register("docRecepExt")}
                    placeholder={t(
                      "clients.form.docRecepExtPlaceholder",
                      "EIN, passport, etc. (max 20)"
                    )}
                    maxLength={20}
                    className={errors.docRecepExt ? "border-destructive" : ""}
                  />
                  {errors.docRecepExt && (
                    <p className="text-sm text-destructive">
                      {errors.docRecepExt.message}
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="legalName">
                {t("clients.form.legalName", "Razón social")} *
              </Label>
              <Input
                id="legalName"
                {...register("legalName")}
                placeholder={t(
                  "clients.form.legalNamePlaceholder",
                  "Legal name (always saved in uppercase)"
                )}
                className={errors.legalName ? "border-destructive" : ""}
              />
              {errors.legalName && (
                <p className="text-sm text-destructive">
                  {errors.legalName.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayName">
                {t("clients.form.displayName", "Display / fantasy name")}
              </Label>
              <Input
                id="displayName"
                {...register("displayName")}
                placeholder={t(
                  "clients.form.displayNamePlaceholder",
                  "Optional – name the company uses publicly"
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">
                {t("clients.form.address", "Address")}
                {documentType === "RUT" ? " *" : null}
              </Label>
              <Input
                id="address"
                className={errors.address ? "border-destructive" : ""}
                {...register("address")}
                placeholder={
                  documentType === "RUT"
                    ? t("clients.form.addressPlaceholderLegal", "Legal business address")
                    : t("clients.form.addressPlaceholder", "Optional")
                }
              />
              {errors.address && (
                <p className="text-sm text-destructive">
                  {errors.address.message}
                </p>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ciudad">
                  {t("clients.form.ciudad", "City")} (
                  {t("clients.form.optionalTag", "optional")})
                </Label>
                <Input
                  id="ciudad"
                  {...register("ciudad")}
                  maxLength={30}
                  placeholder={t("clients.form.ciudadPlaceholder", "Montevideo")}
                  className="transition-colors duration-150"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="departamento">
                  {t("clients.form.departamento", "Department / state")} (
                  {t("clients.form.optionalTag", "optional")})
                </Label>
                <Input
                  id="departamento"
                  {...register("departamento")}
                  maxLength={30}
                  placeholder={t(
                    "clients.form.departamentoPlaceholder",
                    "Montevideo"
                  )}
                  className="transition-colors duration-150"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {t("clients.form.contactInfo", "Contact information")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                {t("clients.form.email", "Email")} *
              </Label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                placeholder={t("clients.form.emailPlaceholder", "client@example.com")}
                className={errors.email ? "border-destructive" : ""}
              />
              {errors.email && (
                <p className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">
                {t("clients.form.phone", "Phone")}
              </Label>
              <Input
                id="phone"
                {...register("phone")}
                placeholder={t("clients.form.phonePlaceholder", "Optional")}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {t("clients.form.languageLabel", "Idioma del cliente")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Controller
              name="language"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  id="client-language"
                  className="flex gap-4"
                  value={field.value ?? "es"}
                  onValueChange={field.onChange}
                >
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <RadioGroupItem value="es" />
                    {t("clients.form.languageEs", "Español")}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <RadioGroupItem value="en" />
                    {t("clients.form.languageEn", "Inglés")}
                  </label>
                </RadioGroup>
              )}
            />
            <p className="text-xs text-muted-foreground">
              {t(
                "clients.form.languageHelp",
                "Define el idioma del nombre del mes en el PDF y de los correos de factura."
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {t("clients.form.invoicingDetails", "Data for invoicing")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t("clients.form.defaultCurrency", "Default currency")}</Label>
              <Controller
                name="defaultCurrency"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? "UYU"}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_CURRENCY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {t("clients.form.paymentType", "Forma de pago (plazos)")}
              </Label>
              <Controller
                name="paymentTermDays"
                control={control}
                render={({ field }) => (
                  <Select
                    value={String(field.value ?? 0)}
                    onValueChange={(v) => field.onChange(Number(v) as 0 | 30 | 60 | 90)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_PAYMENT_TERM_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="discountPercent">
                {t("clients.form.discountPercent", "Discount (%)")}
              </Label>
              <Input
                id="discountPercent"
                type="number"
                min={0}
                max={100}
                step={0.5}
                placeholder={t(
                  "clients.form.discountPercentPlaceholder",
                  "Optional"
                )}
                {...register("discountPercent")}
                className={errors.discountPercent ? "border-destructive" : ""}
              />
              {errors.discountPercent && (
                <p className="text-sm text-destructive">
                  {errors.discountPercent.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">
              {t("clients.form.invoiceEmailCardTitle")}
            </CardTitle>
            <CardDescription>
              {t("clients.form.invoiceEmailCardDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start space-x-2 space-y-0 rounded-md border p-4 transition-colors duration-150">
              <Controller
                name="sendInvoiceByEmail"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="sendInvoiceByEmail"
                    checked={field.value ?? false}
                    onCheckedChange={(checked) =>
                      field.onChange(checked === true)
                    }
                  />
                )}
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="sendInvoiceByEmail"
                  className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t("clients.form.sendInvoiceByEmail", "Send invoice by email")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "clients.form.sendInvoiceByEmailHelp",
                    "Uses the email from contact information."
                  )}
                </p>
              </div>
            </div>
            {watch("sendInvoiceByEmail") === true && (
              <div className="space-y-3">
                <div className="flex items-start space-x-2 space-y-0 rounded-md border p-4 transition-colors duration-150">
                  <Controller
                    name="autoSendInvoiceEmail"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="autoSendInvoiceEmail"
                        checked={field.value ?? true}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                      />
                    )}
                  />
                  <div className="space-y-1 leading-none">
                    <Label
                      htmlFor="autoSendInvoiceEmail"
                      className="cursor-pointer text-sm font-medium leading-none"
                    >
                      {t("clients.form.autoSendInvoiceEmail", "Send automatically")}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "clients.form.autoSendInvoiceEmailHelp",
                        "Send the invoice email automatically when the invoice is created. If disabled, you can send it manually from the invoice detail page."
                      )}
                    </p>
                  </div>
                </div>
                <Label>{t("clients.form.invoiceEmailLabel", "Email for invoice")}</Label>
                <Controller
                  name="invoiceEmailChoice"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value ?? "contact"}
                      onValueChange={(v) => {
                        field.onChange(v);
                        if (v === "contact") {
                          setValue("invoiceEmailCc", []);
                        }
                      }}
                      className="flex flex-col gap-2"
                      aria-label={t("clients.form.invoiceEmailLabel", "Email for invoice")}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="contact"
                          id={`${invoiceEmailRadioId}-contact`}
                        />
                        <Label
                          htmlFor={`${invoiceEmailRadioId}-contact`}
                          className="cursor-pointer font-normal"
                        >
                          {t("clients.form.useContactEmail", "Use contact email")}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="different"
                          id={`${invoiceEmailRadioId}-different`}
                        />
                        <Label
                          htmlFor={`${invoiceEmailRadioId}-different`}
                          className="cursor-pointer font-normal"
                        >
                          {t("clients.form.useDifferentEmail", "Use a different email")}
                        </Label>
                      </div>
                    </RadioGroup>
                  )}
                />
                {watch("invoiceEmailChoice") === "different" && (
                  <div className="space-y-4 pl-6">
                    <div className="space-y-2">
                      <Controller
                        name="invoiceEmail"
                        control={control}
                        render={({ field }) => (
                          <InvoiceEmailAddressListInput
                            id="invoiceEmail"
                            value={field.value ?? []}
                            onChange={field.onChange}
                            placeholder={t(
                              "clients.form.invoiceEmailPlaceholder",
                              "invoice@example.com"
                            )}
                            aria-invalid={Boolean(errors.invoiceEmail)}
                          />
                        )}
                      />
                      {errors.invoiceEmail && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.invoiceEmail.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invoiceEmailCc">{t("clients.form.invoiceEmailCcLabel")}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t("clients.form.invoiceEmailCcHelp")}
                      </p>
                      <Controller
                        name="invoiceEmailCc"
                        control={control}
                        render={({ field }) => (
                          <InvoiceEmailAddressListInput
                            id="invoiceEmailCc"
                            value={field.value ?? []}
                            onChange={field.onChange}
                            placeholder={t("clients.form.invoiceEmailCcPlaceholder")}
                            aria-invalid={Boolean(errors.invoiceEmailCc)}
                          />
                        )}
                      />
                      {errors.invoiceEmailCc && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.invoiceEmailCc.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div className="space-y-2 pt-1">
                  <Label htmlFor="invoiceEmailTemplate">
                    {t("clients.form.invoiceEmailTemplateLabel", "Email template")}
                  </Label>
                  <Controller
                    name="invoiceEmailTemplate"
                    control={control}
                    render={({ field }) => {
                      const raw = field.value ?? "default";
                      const selectValue =
                        raw === "custom" && !canUseInvoiceEmailTemplates
                          ? "default"
                          : raw;
                      return (
                        <Select
                          value={selectValue}
                          onValueChange={(v) => {
                            if (v === "custom" && !canUseInvoiceEmailTemplates) {
                              return;
                            }
                            if (v === "custom" && canUseInvoiceEmailTemplates) {
                              const current =
                                getValues("invoiceEmailCustomBody")?.trim() ?? "";
                              if (current.length === 0) {
                                setValue(
                                  "invoiceEmailCustomBody",
                                  getDefaultInvoiceEmailCustomHtml(t)
                                );
                                setInvoiceEmailBodyEditorKey((k) => k + 1);
                              }
                              const currentSubject =
                                getValues("invoiceEmailCustomSubject")?.trim() ?? "";
                              if (currentSubject.length === 0) {
                                setValue(
                                  "invoiceEmailCustomSubject",
                                  getDefaultInvoiceEmailSubjectTemplate(tenantLang)
                                );
                              }
                            }
                            field.onChange(v);
                          }}
                        >
                          <SelectTrigger
                            id="invoiceEmailTemplate"
                            className="transition-colors duration-150"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">
                              {t("clients.form.invoiceEmailTemplateDefault")}
                            </SelectItem>
                            <SelectItem
                              value="custom"
                              disabled={!canUseInvoiceEmailTemplates}
                              className={
                                !canUseInvoiceEmailTemplates ? "opacity-70" : undefined
                              }
                            >
                              <span>{t("clients.form.invoiceEmailTemplateCustom")}</span>
                              {!canUseInvoiceEmailTemplates ? (
                                <span className="text-muted-foreground">
                                  {" "}
                                  {t("clients.form.invoiceEmailTemplateCustomProOnly")}
                                </span>
                              ) : null}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      );
                    }}
                  />
                </div>
                {canUseInvoiceEmailTemplates &&
                  watch("invoiceEmailTemplate") === "custom" && (
                    <div className="space-y-4">
                      <div className="flex items-start space-x-2 space-y-0 rounded-md border p-4 transition-colors duration-150">
                        <Controller
                          name="invoiceEmailIncludeCompanyLogo"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              id="invoiceEmailIncludeCompanyLogo-edit"
                              checked={field.value ?? true}
                              onCheckedChange={(checked) =>
                                field.onChange(checked === true)
                              }
                              className="transition-colors duration-150"
                            />
                          )}
                        />
                        <div className="grid gap-1.5 leading-none">
                          <Label
                            htmlFor="invoiceEmailIncludeCompanyLogo-edit"
                            className="cursor-pointer text-sm font-medium leading-none text-foreground"
                          >
                            {t("clients.form.invoiceEmailIncludeCompanyLogo")}
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {t("clients.form.invoiceEmailIncludeCompanyLogoHelp")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2 space-y-0 rounded-md border p-4 transition-colors duration-150">
                        <Controller
                          name="invoiceEmailIncludeInvoiceDetails"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              id="invoiceEmailIncludeInvoiceDetails-edit"
                              checked={field.value ?? true}
                              onCheckedChange={(checked) =>
                                field.onChange(checked === true)
                              }
                              className="transition-colors duration-150"
                            />
                          )}
                        />
                        <div className="grid gap-1.5 leading-none">
                          <Label
                            htmlFor="invoiceEmailIncludeInvoiceDetails-edit"
                            className="cursor-pointer text-sm font-medium leading-none text-foreground"
                          >
                            {t("clients.form.invoiceEmailIncludeInvoiceDetails")}
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {t("clients.form.invoiceEmailIncludeInvoiceDetailsHelp")}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="invoiceEmailCustomSubject-edit">
                          {t("clients.form.invoiceEmailCustomSubjectLabel", "Email subject")}
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {t("clients.form.invoiceEmailCustomSubjectHelp")}
                        </p>
                        <Input
                          id="invoiceEmailCustomSubject-edit"
                          {...register("invoiceEmailCustomSubject")}
                          maxLength={255}
                          className="transition-colors duration-150"
                          aria-invalid={Boolean(errors.invoiceEmailCustomSubject)}
                          autoComplete="off"
                        />
                        <div className="flex justify-end">
                          <p className="text-xs text-muted-foreground tabular-nums">
                            {(watch("invoiceEmailCustomSubject") ?? "").length} / 255
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t("clients.form.pdfFilenameTokens", "Etiquetas disponibles:")}{" "}
                          {["{series}", "{number}", "{companyName}"].map(
                            (tok) => (
                              <code
                                key={tok}
                                className="mr-1 rounded bg-muted px-1 py-0.5 font-mono"
                              >
                                {tok}
                              </code>
                            )
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t("clients.form.invoiceEmailSubjectPreview", "Vista previa:")}{" "}
                          <span className="font-mono text-foreground">
                            {previewInvoiceEmailSubject(
                              watch("invoiceEmailCustomSubject"),
                              {
                                language: watch("language") ?? "es",
                                companyName: tenant?.name?.trim() || "Tu empresa",
                              }
                            )}
                          </span>
                        </p>
                        {errors.invoiceEmailCustomSubject && (
                          <p className="text-sm text-destructive">
                            {errors.invoiceEmailCustomSubject.message}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="invoiceEmailCustomBody-edit">
                          {t("clients.form.invoiceEmailCustomBodyLabel", "Custom message")}
                        </Label>
                        <Controller
                          name="invoiceEmailCustomBody"
                          control={control}
                          render={({ field }) => (
                            <InvoiceEmailCustomBodyEditor
                              key={invoiceEmailBodyEditorKey}
                              initialHtml={field.value ?? ""}
                              onHtmlChange={field.onChange}
                              onBlur={field.onBlur}
                              disabled={updateMutation.isPending}
                              hasError={Boolean(errors.invoiceEmailCustomBody)}
                              placeholder={t(
                                "clients.form.invoiceEmailCustomBodyPlaceholder"
                              )}
                              toolbarAriaLabel={t(
                                "clients.form.invoiceEmailCustomBodyToolbarAria"
                              )}
                              editorId="invoiceEmailCustomBody-edit"
                            />
                          )}
                        />
                        <div className="flex justify-end">
                          <p className="text-xs text-muted-foreground tabular-nums">
                            {htmlVisibleLength(watch("invoiceEmailCustomBody"))} / 2000
                          </p>
                        </div>
                        {errors.invoiceEmailCustomBody && (
                          <p className="text-sm text-destructive">
                            {errors.invoiceEmailCustomBody.message}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            )}
            {watch("sendInvoiceByEmail") === true && canUseInvoiceEmailTemplates ? (
              <div className="flex justify-end border-t border-border pt-4">
                <InvoiceEmailPreviewButton
                  getPayload={() => {
                    const template = watch("invoiceEmailTemplate") ?? "default";
                    if (template === "custom") {
                      return {
                        invoiceEmailTemplate: "custom",
                        invoiceEmailCustomBody:
                          watch("invoiceEmailCustomBody") ?? undefined,
                        invoiceEmailIncludeCompanyLogo:
                          watch("invoiceEmailIncludeCompanyLogo") ?? true,
                        invoiceEmailIncludeInvoiceDetails:
                          watch("invoiceEmailIncludeInvoiceDetails") ?? true,
                      };
                    }
                    return {
                      invoiceEmailTemplate: template,
                      invoiceEmailCustomBody: undefined,
                    };
                  }}
                  disabled={updateMutation.isPending}
                />
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {t("clients.form.pdfFilenameTitle", "Nombre del archivo PDF")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t(
                "clients.form.pdfFilenameHelp",
                "Personalizá el nombre del PDF al descargarlo o enviarlo a este cliente. Dejá vacío para usar el nombre por defecto."
              )}
            </p>
            {/* Renders nothing when the tenant has no marcas. */}
            <Controller
              control={control}
              name="defaultBrandId"
              render={({ field }) => (
                <BrandSelectField
                  value={field.value ?? ""}
                  onChange={field.onChange}
                />
              )}
            />
            <div className="space-y-2">
              <Label htmlFor="pdfFilenameTemplate-edit">
                {t("clients.form.pdfFilenameLabel", "Formato del nombre")}
              </Label>
              <Input
                id="pdfFilenameTemplate-edit"
                {...register("pdfFilenameTemplate")}
                placeholder="{cliente}_{fecha}_{numero}"
                maxLength={255}
                aria-invalid={Boolean(errors.pdfFilenameTemplate)}
              />
              <p className="text-xs text-muted-foreground">
                {t("clients.form.pdfFilenameTokens", "Etiquetas disponibles:")}{" "}
                {PDF_FILENAME_TOKENS.map((tok) => (
                  <code
                    key={tok}
                    className="mr-1 rounded bg-muted px-1 py-0.5 font-mono"
                  >{`{${tok}}`}</code>
                ))}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  "clients.form.pdfFilenameDateNote",
                  "El mes y la fecha se toman de la fecha de cada factura."
                )}
              </p>
              {(() => {
                const preview = previewPdfFilename(
                  watch("pdfFilenameTemplate"),
                  {
                    language: watch("language") ?? "es",
                    cliente:
                      watch("displayName")?.trim() ||
                      watch("legalName")?.trim() ||
                      undefined,
                    rut: watch("rut")?.trim() || undefined,
                  }
                );
                return preview ? (
                  <p className="text-xs text-muted-foreground">
                    {t("clients.form.pdfFilenamePreview", "Vista previa:")}{" "}
                    <span className="font-mono text-foreground">{preview}</span>
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "clients.form.pdfFilenameDefaultPreview",
                      "Por defecto: CFE-101-A-1234.pdf"
                    )}
                  </p>
                );
              })()}
              {errors.pdfFilenameTemplate && (
                <p className="text-sm text-destructive">
                  {errors.pdfFilenameTemplate.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-row justify-between pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
            >
              {t("clients.form.cancel", "Cancel")}
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending
                ? t("clients.form.saving", "Saving…")
                : t("clients.form.save", "Save")}
            </Button>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
